import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router-dom'
import { useLocale } from '../i18n/LocaleProvider'
import type { Locale } from '../i18n/locale'
import { localizedRoutes, stripLocale } from '../i18n/paths'


function browserLanguage(): Locale {
  return /^ja(?:-|$)/i.test(navigator.language || navigator.languages?.[0] || '') ? 'ja' : 'en'
}

export default function LanguageSelector() {
  const { locale, setLanguagePreference } = useLocale()
  const { pathname } = useLocation()
  const [suggested, setSuggested] = useState<Locale | null>(null)
  const [dismissed, setDismissed] = useState(false)
  const control = useRef<HTMLSelectElement>(null)
  const prompt = useRef<HTMLDivElement>(null)
  const [anchor, setAnchor] = useState<{ host: HTMLElement; top: number; right: number } | null>(null)

  useEffect(() => {
    const host = control.current?.closest<HTMLElement>('.navbar-wrap')
    const trigger = control.current?.closest<HTMLElement>('.navbar-language')
    if (!host || !trigger) return
    // Render beside the navbar: nesting backdrop filters blocks the page backdrop.
    const position = () => {
      const outer = host.getBoundingClientRect()
      const button = trigger.getBoundingClientRect()
      setAnchor({ host, top: button.bottom - outer.top + 18, right: outer.right - button.right })
    }
    position()
    const observer = new ResizeObserver(position)
    observer.observe(host)
    observer.observe(trigger)
    window.addEventListener('resize', position)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', position)
    }
  }, [])

  useEffect(() => {
    const detect = () => setSuggested(browserLanguage())
    detect()
    window.addEventListener('languagechange', detect)
    return () => window.removeEventListener('languagechange', detect)
  }, [])

  const dismiss = () => {
    // Restore focus only when a focused prompt control is about to disappear.
    if (prompt.current?.contains(document.activeElement)) control.current?.focus()
    setDismissed(true)
  }

  const supported = localizedRoutes.includes(stripLocale(pathname).replace(/\/+$/, '') || '/')
  const visible = supported && suggested !== null && suggested !== locale && !dismissed
  useEffect(() => {
    if (!visible) return
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dismiss()
    }
    window.addEventListener('keydown', escape)
    return () => window.removeEventListener('keydown', escape)
  }, [visible])

  const choose = (next: Locale) => {
    dismiss()
    setLanguagePreference(next)
  }
  const japanese = suggested === 'ja'

  return (
    <div className="navbar-language">
      <label className="navbar-language-trigger">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
          <circle cx="12" cy="12" r="9" />
          <ellipse cx="12" cy="12" rx="4" ry="9" />
          <path d="M3 12h18M5 6.5h14M5 17.5h14" />
        </svg>
        <select
          ref={control}
          aria-label={locale === 'ja' ? '表示言語' : 'Display language'}
          value={locale}
          onChange={event => choose(event.target.value as Locale)}
        >
          <option value="en" lang="en">English</option>
          <option value="ja" lang="ja">日本語</option>
        </select>
      </label>
      {visible && anchor && createPortal(
        <div ref={prompt} style={{ top: anchor.top, right: anchor.right }} className="language-suggestion" role="dialog" aria-label={japanese ? '表示言語のご案内' : 'Language suggestion'} lang={suggested}>
          <button type="button" className="language-suggestion-close" aria-label={japanese ? '閉じる' : 'Dismiss'} onClick={dismiss}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d="m4 4 8 8M12 4l-8 8" /></svg>
          </button>
          <p>{japanese ? '日本語のページに切り替えますか？' : 'Would you like to view this page in English?'}</p>
          <div className="language-suggestion-actions">
            <button type="button" className="language-suggestion-switch" onClick={() => choose(suggested)}>
              {japanese ? '日本語に切り替える' : 'Switch to English'}
            </button>
            <button type="button" onClick={dismiss}>{japanese ? '英語のまま見る' : 'Stay in Japanese'}</button>
          </div>
        </div>,
        anchor.host
      )}
    </div>
  )
}
